import { useEffect, useState } from "react";
import { ImageOff } from "lucide-react";
import { loadSession } from "../lib/auth";

/**
 * Bild aus einer geschützten Datei.
 *
 * Kundenfotos liegen in einem privaten Speicher; der Server gibt sie nur mit
 * Anmelde-Token heraus. Ein einfaches <img src="/v1/admin/files/...">
 * funktioniert deshalb NICHT - der Browser schickt bei Bildanfragen keinen
 * Authorization-Header mit, die Antwort ist 401 und das Bild bleibt leer.
 *
 * Also holen wir die Datei per fetch mit Token und zeigen sie über eine
 * lokale Blob-Adresse an.
 */
export function GeschuetztesBild({
  fileId,
  alt = "",
  className = "",
  onOeffnen,
}: {
  fileId: string;
  alt?: string;
  className?: string;
  onOeffnen?: (url: string) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState(false);

  useEffect(() => {
    let abgebrochen = false;
    let erzeugt: string | null = null;

    (async () => {
      const s = loadSession();
      if (!s) { setFehler(true); return; }
      try {
        const res = await fetch(`/v1/admin/files/${fileId}`, {
          headers: { Authorization: `Bearer ${s.accessToken}` },
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (abgebrochen) return;
        erzeugt = URL.createObjectURL(blob);
        setUrl(erzeugt);
      } catch {
        if (!abgebrochen) setFehler(true);
      }
    })();

    return () => {
      abgebrochen = true;
      // Speicher wieder freigeben, sonst sammeln sich die Blobs an.
      if (erzeugt) URL.revokeObjectURL(erzeugt);
    };
  }, [fileId]);

  if (fehler) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-slate-400 ${className}`}>
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }
  if (!url) return <div className={`animate-pulse bg-slate-200 ${className}`} />;

  return (
    <img
      src={url}
      alt={alt}
      className={className}
      onClick={onOeffnen ? () => onOeffnen(url) : undefined}
    />
  );
}

/**
 * Geschützte Datei in einem neuen Tab öffnen (PDF, Bild ...).
 *
 * Aus demselben Grund wie oben: ein normaler Link schickt keinen
 * Anmelde-Token mit und liefe auf 401. Also holen, zwischenspeichern, öffnen.
 */
export async function geschuetzteDateiOeffnen(fileId: string): Promise<void> {
  const s = loadSession();
  if (!s) return;
  try {
    const res = await fetch(`/v1/admin/files/${fileId}`, {
      headers: { Authorization: `Bearer ${s.accessToken}` },
    });
    if (!res.ok) throw new Error(String(res.status));
    const url = URL.createObjectURL(await res.blob());
    window.open(url, "_blank", "noopener");
    // Nach dem Öffnen wieder freigeben - der Tab hat die Daten dann schon.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    alert("Die Datei konnte nicht geöffnet werden.");
  }
}
